import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { STRUTTURA_CONFIG } from "../config/strutturaConfig";
import {
  Building2,
  Save,
  MapPin,
  Phone,
  Mail,
  Globe,
  FileText,
  CreditCard,
  User,
  Loader2,
  Lock,
  Facebook
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dati default quando Zoho non è disponibile
const DEFAULT_STRUTTURA = {
  id: "",
  nome: "",
  codiceStruttura: "",
  tipologia: "",
  postiLetto: 0,
  indirizzo: "",
  numeroCivico: "",
  cap: "",
  citta: "",
  provincia: "",
  telefono: "",
  email: "",
  facebook: "",
  sitoWeb: "",
  tariffaDoppiaWc: 0,
  tariffaSingolaWc: 0,
  // Responsabili
  responsabileStruttura: "",
  responsabileStrutturaEmail: "",
  assistenteSociale: "",
  assistenteSocialeEmail: "",
  educatoreProfessionale: "",
  educatoreProfessionaleEmail: "",
  // Dati Societari
  nomeSocieta: "",
  viaFatturazione: "",
  cittaFatturazione: "",
  capFatturazione: "",
  provinciaFatturazione: "",
  partitaIva: "",
  codiceFiscale: "",
  pec: "",
  sdi: "",
  // Dati Bancari
  banca: "",
  iban: "",
  swift: "",
  intestatario: ""
};

export default function ProfiloStruttura() {
  const { token, activeStruttura, refreshUser, user } = useAuth();
  const [dati, setDati] = useState(DEFAULT_STRUTTURA);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Mappa risposta Zoho al formato dati del form
  const mapZohoToDati = (z, codiceStruttura) => ({
    id: z.id || codiceStruttura,
    nome: z.nome || "",
    codiceStruttura: z.id || codiceStruttura,
    tipologia: z.tipologia || "",
    postiLetto: z.posti_totali || 0,
    indirizzo: z.indirizzo || "",
    numeroCivico: z.numero_civico || "",
    cap: z.cap || "",
    citta: z.comune || "",
    provincia: z.provincia || "",
    telefono: z.telefono || "",
    email: z.email || "",
    facebook: z.facebook || "",
    sitoWeb: z.sito_web || "",
    tariffaDoppiaWc: 0,
    tariffaSingolaWc: 0,
    responsabileStruttura: z.responsabile_struttura || "",
    responsabileStrutturaEmail: z.responsabile_struttura_email || "",
    assistenteSociale: z.assistente_sociale || "",
    assistenteSocialeEmail: z.assistente_sociale_email || "",
    educatoreProfessionale: z.educatore_professionale || "",
    educatoreProfessionaleEmail: z.educatore_professionale_email || "",
    nomeSocieta: z.nome_societa || "",
    viaFatturazione: z.via_fatturazione || "",
    cittaFatturazione: z.citta_fatturazione || "",
    capFatturazione: z.cap_fatturazione || "",
    provinciaFatturazione: z.provincia_fatturazione || "",
    partitaIva: z.partita_iva || "",
    codiceFiscale: z.codice_fiscale || "",
    pec: z.pec || "",
    sdi: z.sdi || "",
    banca: z.banca || "",
    iban: z.iban || "",
    swift: z.swift || "",
    intestatario: z.intestatario || ""
  });

  // Mappa risposta API strutture (DB) al formato dati del form
  const mapApiStrutturaToDati = (s) => ({
    id: s.id || "",
    nome: s.name || "",
    codiceStruttura: s.zohoRecordId || s.id || "",
    tipologia: s.tipoStruttura || "",
    postiLetto: s.postiTotali || 0,
    indirizzo: s.via || "",
    numeroCivico: s.numeroCivico || "",
    cap: s.cap || "",
    citta: s.comune || "",
    provincia: s.provincia || "",
    telefono: s.telefonoStruttura || "",
    email: s.email || "",
    facebook: s.facebook || "",
    sitoWeb: s.sitoWeb || "",
    tariffaDoppiaWc: s.tariffaDoppiaWc ?? 0,
    tariffaSingolaWc: s.tariffaSingolaWc ?? 0,
    responsabileStruttura: s.responsabileStruttura || "",
    responsabileStrutturaEmail: s.responsabileStrutturaEmail || "",
    assistenteSociale: s.assistenteSociale || "",
    assistenteSocialeEmail: s.assistenteSocialeEmail || "",
    educatoreProfessionale: s.educatoreProfessionale || "",
    educatoreProfessionaleEmail: s.educatoreProfessionaleEmail || "",
    nomeSocieta: s.nomeSocieta || "",
    viaFatturazione: s.viaFatturazione || "",
    cittaFatturazione: s.cittaFatturazione || "",
    capFatturazione: s.capFatturazione || "",
    provinciaFatturazione: s.provinciaFatturazione || "",
    partitaIva: s.partitaIva || "",
    codiceFiscale: s.codiceFiscale || "",
    pec: s.pec || "",
    sdi: s.sdi || "",
    banca: s.banca || "",
    iban: s.iban || "",
    swift: s.swift || "",
    intestatario: s.intestatario || ""
  });

  const resolveStrutturaSource = () => {
    const isMongoId = /^[a-f0-9]{24}$/i.test(activeStruttura?.id || "");
    const mongoId =
      (isMongoId ? activeStruttura.id : null) ||
      (/^[a-f0-9]{24}$/i.test(user?.struttura_id || "") ? user.struttura_id : null);
    const zohoId =
      (!mongoId &&
        (activeStruttura?.zohoRecordId ||
          STRUTTURA_CONFIG.zohoId ||
          (!isMongoId ? activeStruttura?.id : null))) ||
      null;
    return { zohoId, mongoId };
  };

  // Preferire sempre Mongo (API strutture); Zoho solo come fallback legacy
  const fetchStrutturaData = async () => {
    if (!activeStruttura?.id && !user?.struttura_id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { zohoId, mongoId } = resolveStrutturaSource();

      if (mongoId) {
        const response = await axios.get(`${API_URL}/api/strutture/${mongoId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          setDati(mapApiStrutturaToDati(response.data));
        }
      } else if (zohoId) {
        const response = await axios.get(`${API_URL}/api/zoho/struttura/${zohoId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          setDati(mapZohoToDati(response.data, zohoId));
        }
      }
    } catch (error) {
      console.error("Errore caricamento dati struttura:", error);
      if (error.response?.status === 503) {
        toast.error("Impossibile caricare i dati della struttura");
      } else if (error.response?.status === 404) {
        toast.error("Struttura non trovata");
      } else {
        toast.error("Errore nel caricamento dei dati");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStrutturaData();
  }, [token, activeStruttura?.id]);

  const handleChange = (field, value) => {
    setDati(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!activeStruttura?.id && !user?.struttura_id) return;
    setSaving(true);
    try {
      const { zohoId, mongoId } = resolveStrutturaSource();

      if (mongoId) {
        const updateData = {
          name: dati.nome,
          tipoStruttura: dati.tipologia,
          postiTotali: parseInt(dati.postiLetto) || 0,
          telefonoStruttura: dati.telefono,
          comune: dati.citta,
          via: dati.indirizzo,
          numeroCivico: dati.numeroCivico,
          cap: dati.cap || null,
          provincia: dati.provincia,
          email: dati.email || null,
          facebook: dati.facebook || null,
          tariffaDoppiaWc: parseFloat(dati.tariffaDoppiaWc) || 0,
          tariffaSingolaWc: parseFloat(dati.tariffaSingolaWc) || 0,
          sitoWeb: dati.sitoWeb || null,
          responsabileStruttura: dati.responsabileStruttura || null,
          responsabileStrutturaEmail: dati.responsabileStrutturaEmail || null,
          assistenteSociale: dati.assistenteSociale || null,
          assistenteSocialeEmail: dati.assistenteSocialeEmail || null,
          educatoreProfessionale: dati.educatoreProfessionale || null,
          educatoreProfessionaleEmail: dati.educatoreProfessionaleEmail || null,
          nomeSocieta: dati.nomeSocieta || null,
          viaFatturazione: dati.viaFatturazione || null,
          cittaFatturazione: dati.cittaFatturazione || null,
          capFatturazione: dati.capFatturazione || null,
          provinciaFatturazione: dati.provinciaFatturazione || null,
          partitaIva: dati.partitaIva || null,
          codiceFiscale: dati.codiceFiscale || null,
          pec: dati.pec || null,
          sdi: dati.sdi || null,
          banca: dati.banca || null,
          iban: dati.iban || null,
          swift: dati.swift || null,
          intestatario: dati.intestatario || null
        };
        await axios.put(
          `${API_URL}/api/strutture/${mongoId}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Dati salvati.");
        try {
          await refreshUser();
        } catch {
          /* ignore */
        }
      } else if (zohoId) {
        const updateData = {
          nome: dati.nome,
          tipologia: dati.tipologia,
          posti_letto: parseInt(dati.postiLetto) || 0,
          indirizzo: dati.indirizzo,
          numero_civico: dati.numeroCivico,
          cap: dati.cap,
          citta: dati.citta,
          provincia: dati.provincia,
          telefono: dati.telefono,
          email: dati.email,
          facebook: dati.facebook,
          sito_web: dati.sitoWeb,
          responsabile_struttura: dati.responsabileStruttura,
          responsabile_struttura_email: dati.responsabileStrutturaEmail,
          assistente_sociale: dati.assistenteSociale,
          assistente_sociale_email: dati.assistenteSocialeEmail,
          educatore_professionale: dati.educatoreProfessionale,
          educatore_professionale_email: dati.educatoreProfessionaleEmail,
          nome_societa: dati.nomeSocieta,
          via_fatturazione: dati.viaFatturazione,
          citta_fatturazione: dati.cittaFatturazione,
          cap_fatturazione: dati.capFatturazione,
          provincia_fatturazione: dati.provinciaFatturazione,
          partita_iva: dati.partitaIva,
          codice_fiscale: dati.codiceFiscale,
          pec: dati.pec,
          sdi: dati.sdi,
          banca: dati.banca,
          iban: dati.iban,
          swift: dati.swift,
          intestatario: dati.intestatario
        };
        await axios.put(
          `${API_URL}/api/zoho/struttura/${zohoId}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Dati salvati.");
        try {
          await refreshUser();
        } catch {
          /* ignore */
        }
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Errore salvataggio:", error);
      const errorMsg = error.response?.data?.detail || "Errore nel salvataggio";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#4CAF50] mx-auto mb-3" />
          <p className="text-slate-500">Caricamento dati struttura...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Page Header - responsive */}
      <PageHeader
        title="Profilo Struttura"
        subtitle="Gestisci i dati della tua struttura"
        icon={Building2}
        iconContainerClassName="bg-[#4CAF50]/10"
        iconClassName="text-[#4CAF50]"
      />

      {/* Action Buttons */}
      <div className="flex justify-end mb-6">
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Annulla
              </Button>
              <Button 
                onClick={handleSave} 
                className="bg-[#4CAF50] hover:bg-[#43A047]"
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Salva
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)} className="bg-[#4CAF50] hover:bg-[#43A047]">
              Modifica
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="struttura">
        <TabsList className="grid w-full grid-cols-3 h-12 mb-6">
          <TabsTrigger value="struttura" data-testid="tab-dati-struttura">Dati Struttura</TabsTrigger>
          <TabsTrigger value="societari" data-testid="tab-dati-societari">Dati Societari</TabsTrigger>
          <TabsTrigger value="bancari" data-testid="tab-dati-bancari">Dati Bancari</TabsTrigger>
        </TabsList>

        {/* Tab Dati Struttura */}
        <TabsContent value="struttura">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#4CAF50]" />
                Informazioni Struttura
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome Struttura</Label>
                  <Input
                    value={dati.nome}
                    onChange={(e) => handleChange("nome", e.target.value)}
                    disabled={!isEditing}
                    className="h-11"
                    data-testid="input-nome-struttura"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Codice Struttura
                    <Lock className="w-3 h-3 text-slate-400" />
                  </Label>
                  <Input
                    value={dati.codiceStruttura}
                    disabled={true}
                    className="h-11 bg-slate-100 font-mono text-sm"
                    data-testid="input-codice-struttura"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipologia</Label>
                  <Input
                    value={dati.tipologia}
                    onChange={(e) => handleChange("tipologia", e.target.value)}
                    disabled={!isEditing}
                    className="h-11"
                    data-testid="input-tipologia"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Posti Letto Totali</Label>
                  <Input
                    type="number"
                    value={dati.postiLetto}
                    onChange={(e) => handleChange("postiLetto", e.target.value)}
                    disabled={!isEditing}
                    className="h-11"
                    data-testid="input-posti-letto"
                  />
                </div>
              </div>

              <div className="border-t pt-5">
                <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  Indirizzo
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-8 space-y-2">
                    <Label>Via</Label>
                    <Input
                      value={dati.indirizzo}
                      onChange={(e) => handleChange("indirizzo", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                  <div className="md:col-span-4 space-y-2">
                    <Label>N.</Label>
                    <Input
                      value={dati.numeroCivico}
                      onChange={(e) => handleChange("numeroCivico", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                  <div className="md:col-span-3 space-y-2">
                    <Label>CAP Struttura</Label>
                    <Input
                      value={dati.cap}
                      onChange={(e) => handleChange("cap", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                  <div className="md:col-span-6 space-y-2">
                    <Label>Città</Label>
                    <Input
                      value={dati.citta}
                      onChange={(e) => handleChange("citta", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                  <div className="md:col-span-3 space-y-2">
                    <Label>Provincia</Label>
                    <Input
                      value={dati.provincia}
                      onChange={(e) => handleChange("provincia", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-5">
                <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-500" />
                  Contatti
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Telefono</Label>
                    <Input
                      value={dati.telefono}
                      onChange={(e) => handleChange("telefono", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={dati.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Facebook className="w-4 h-4 text-blue-600" />
                      Facebook
                    </Label>
                    <Input
                      value={dati.facebook}
                      onChange={(e) => handleChange("facebook", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-500" />
                      Sito Web
                    </Label>
                    <Input
                      value={dati.sitoWeb}
                      onChange={(e) => handleChange("sitoWeb", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-5">
                <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-500" />
                  Responsabili
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Responsabile Struttura</Label>
                    <Input
                      value={dati.responsabileStruttura}
                      onChange={(e) => handleChange("responsabileStruttura", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                    <Input
                      type="email"
                      placeholder="Email responsabile struttura"
                      value={dati.responsabileStrutturaEmail}
                      onChange={(e) => handleChange("responsabileStrutturaEmail", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Assistente Sociale</Label>
                    <Input
                      value={dati.assistenteSociale}
                      onChange={(e) => handleChange("assistenteSociale", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                    <Input
                      type="email"
                      placeholder="Email assistente sociale"
                      value={dati.assistenteSocialeEmail}
                      onChange={(e) => handleChange("assistenteSocialeEmail", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Educatore Professionale</Label>
                    <Input
                      value={dati.educatoreProfessionale}
                      onChange={(e) => handleChange("educatoreProfessionale", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                    <Input
                      type="email"
                      placeholder="Email educatore professionale"
                      value={dati.educatoreProfessionaleEmail}
                      onChange={(e) => handleChange("educatoreProfessionaleEmail", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-3">
                  Le email di assistente sociale ed educatore professionale ricevono una notifica automatica ad ogni nuovo ingresso ospite.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Dati Societari */}
        <TabsContent value="societari">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#4CAF50]" />
                Informazioni Società
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label>Nome Società</Label>
                  <Input
                    value={dati.nomeSocieta}
                    onChange={(e) => handleChange("nomeSocieta", e.target.value)}
                    disabled={!isEditing}
                    className="h-11"
                    data-testid="input-nome-societa"
                  />
                </div>
              </div>

              <div className="border-t pt-5">
                <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  Sede Legale / Fatturazione
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-12 space-y-2">
                    <Label>Via Fatturazione</Label>
                    <Input
                      value={dati.viaFatturazione}
                      onChange={(e) => handleChange("viaFatturazione", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                      data-testid="input-via-fatturazione"
                    />
                  </div>
                  <div className="md:col-span-3 space-y-2">
                    <Label>CAP</Label>
                    <Input
                      value={dati.capFatturazione}
                      onChange={(e) => handleChange("capFatturazione", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                      data-testid="input-cap-fatturazione"
                    />
                  </div>
                  <div className="md:col-span-6 space-y-2">
                    <Label>Città</Label>
                    <Input
                      value={dati.cittaFatturazione}
                      onChange={(e) => handleChange("cittaFatturazione", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                      data-testid="input-citta-fatturazione"
                    />
                  </div>
                  <div className="md:col-span-3 space-y-2">
                    <Label>Provincia</Label>
                    <Input
                      value={dati.provinciaFatturazione}
                      onChange={(e) => handleChange("provinciaFatturazione", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                      data-testid="input-provincia-fatturazione"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-5">
                <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-500" />
                  Dati Fiscali
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Partita IVA</Label>
                    <Input
                      value={dati.partitaIva}
                      onChange={(e) => handleChange("partitaIva", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Codice Fiscale</Label>
                    <Input
                      value={dati.codiceFiscale}
                      onChange={(e) => handleChange("codiceFiscale", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>PEC</Label>
                    <Input
                      type="email"
                      value={dati.pec}
                      onChange={(e) => handleChange("pec", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SDI (Codice Destinatario)</Label>
                    <Input
                      value={dati.sdi}
                      onChange={(e) => handleChange("sdi", e.target.value)}
                      disabled={!isEditing}
                      className="h-11"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Dati Bancari */}
        <TabsContent value="bancari">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#4CAF50]" />
                Dati Bancari
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label>Banca</Label>
                  <Input
                    value={dati.banca}
                    onChange={(e) => handleChange("banca", e.target.value)}
                    disabled={!isEditing}
                    className="h-11"
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label>IBAN</Label>
                  <Input
                    value={dati.iban}
                    onChange={(e) => handleChange("iban", e.target.value)}
                    disabled={!isEditing}
                    className="h-11 font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SWIFT/BIC</Label>
                  <Input
                    value={dati.swift}
                    onChange={(e) => handleChange("swift", e.target.value)}
                    disabled={!isEditing}
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Intestatario</Label>
                  <Input
                    value={dati.intestatario}
                    onChange={(e) => handleChange("intestatario", e.target.value)}
                    disabled={!isEditing}
                    className="h-11"
                  />
                </div>
              </div>

              <div className="p-4 bg-amber-50 rounded-xl mt-4">
                <p className="text-sm text-amber-700">
                  <strong>Importante:</strong> I dati bancari sono utilizzati per l'accredito dei gettoni maturati dalla sezione "Ospiti in Rete".
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
